import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    Dashboard, 
    DashboardItem, 
    Chart, 
    Report, 
    ReportQueryResult, 
    GridPosition 
} from '@bi-studio/shared/lib';
import { 
    getDashboard, 
    renderDashboard, 
    executeReport,
    getCharts,
    getReports
} from '@bi-studio/shared/apis';
import { RootState } from '../index';

export interface RenderedDashboardItem extends DashboardItem {
    chart: Chart | null;
    report: Report | null;
    data: ReportQueryResult;
}

interface DashboardState {
    dashboard: (Dashboard & { items?: DashboardItem[] }) | null;
    renderedItems: RenderedDashboardItem[];
    availableCharts: Chart[];
    availableReports: Report[];
    chartLayouts: Record<string, GridPosition>;
    isLoading: boolean;
    isRendering: boolean;
    isLoadingComponents: boolean;
    error: string | null;
    autoRefreshEnabled: boolean;
    lastRefreshDate: string | null;
}

const initialState: DashboardState = {
    dashboard: null,
    renderedItems: [],
    availableCharts: [],
    availableReports: [],
    chartLayouts: {},
    isLoading: false,
    isRendering: false,
    isLoadingComponents: false,
    error: null,
    autoRefreshEnabled: false,
    lastRefreshDate: null,
};

export const fetchDashboardDetails = createAsyncThunk(
    'dashboard/fetchDetails',
    async ({ token, dashboardId }: { token: string; dashboardId: string }, { rejectWithValue }) => {
        try {
            const dashboard = await getDashboard(token, dashboardId);
            return dashboard;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch dashboard details');
        }
    }
);

export const performRenderDashboard = createAsyncThunk(
    'dashboard/render',
    async ({ token, dashboardId }: { token: string; dashboardId: string }, { rejectWithValue }) => {
        try {
            const result = await renderDashboard(token, dashboardId);
            const validItems = result.items.filter(
                (item: any) => (item.chart || item.report)
            ) as RenderedDashboardItem[];
            return validItems;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to render dashboard');
        }
    }
);

export const refreshDashboardItem = createAsyncThunk(
    'dashboard/refreshItem',
    async (
        { token, itemId, reportId, page }: { 
            token: string; 
            itemId: string; 
            reportId: string; 
            page: number 
        }, 
        { rejectWithValue }
    ) => {
        try {
            const data = await executeReport(token, reportId, {
                pagination: { page, pageSize: 50 },
                cache: { enabled: false }
            });
            return { itemId, data };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to refresh dashboard item');
        }
    }
);

export const fetchAvailableComponents = createAsyncThunk(
    'dashboard/fetchAvailableComponents',
    async (token: string, { rejectWithValue }) => {
        try {
            const [charts, reports] = await Promise.all([
                getCharts(token),
                getReports(token)
            ]);
            return { charts, reports };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch components');
        }
    }
);


export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setAutoRefreshEnabled: (state, action: PayloadAction<boolean>) => {
            state.autoRefreshEnabled = action.payload;
        },
        resetDashboardState: () => initialState,
        setDashboardError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        updateLayout: (state, action: PayloadAction<{ itemId: string; layout: GridPosition }>) => {
            state.chartLayouts[action.payload.itemId] = action.payload.layout;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDashboardDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.dashboard = action.payload;
                
                // Extract layouts
                const layouts: Record<string, GridPosition> = {};
                if (action.payload.items) {
                    action.payload.items.forEach(item => {
                        if (item.layout) {
                            const key = item.chartId || item.reportId;
                            if (key) layouts[key] = item.layout;
                        }
                    });
                }
                state.chartLayouts = layouts;
            })
            .addCase(fetchDashboardDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(performRenderDashboard.pending, (state) => {
                state.isRendering = true;
                state.error = null;
            })
            .addCase(performRenderDashboard.fulfilled, (state, action) => {
                state.isRendering = false;
                state.renderedItems = action.payload;
                state.lastRefreshDate = new Date().toISOString();
            })
            .addCase(performRenderDashboard.rejected, (state, action) => {
                state.isRendering = false;
                state.error = action.payload as string;
            })
            .addCase(refreshDashboardItem.fulfilled, (state, action) => {
                state.renderedItems = state.renderedItems.map(item => 
                    item.id === action.payload.itemId ? { ...item, data: action.payload.data } : item
                );
            })
            .addCase(fetchAvailableComponents.pending, (state) => {
                state.isLoadingComponents = true;
            })
            .addCase(fetchAvailableComponents.fulfilled, (state, action) => {
                state.isLoadingComponents = false;
                state.availableCharts = action.payload.charts;
                state.availableReports = action.payload.reports;
            })
            .addCase(fetchAvailableComponents.rejected, (state, action) => {
                state.isLoadingComponents = false;
                state.error = action.payload as string;
            });
    }
});

export const { 
    setAutoRefreshEnabled, 
    resetDashboardState, 
    setDashboardError,
    updateLayout 
} = dashboardSlice.actions;

// Selectors
export const selectDashboardFull = (state: RootState) => state.dashboard;
export const selectDashboardMetadata = (state: RootState) => state.dashboard.dashboard;
export const selectRenderedItems = (state: RootState) => state.dashboard.renderedItems;
export const selectDashboardLoading = (state: RootState) => state.dashboard.isLoading;
export const selectDashboardRendering = (state: RootState) => state.dashboard.isRendering;
export const selectDashboardError = (state: RootState) => state.dashboard.error;
export const selectDashboardLayouts = (state: RootState) => state.dashboard.chartLayouts;

export default dashboardSlice.reducer;
